import { PlusIcon, MinusIcon, type LucideIcon } from 'lucide-react'
import { createContext, useContext, type ReactNode } from 'react'
import { Markdown } from '@highlight/ui'

// Host content projected into a marker. Uses context, not extension options:
// Tiptap builds extensions once at mount, so post-mount options never reach the
// NodeView; context updates do. The read-only renderer reads it too.
export const ContentSlotContext = createContext<ReactNode | null>(null)

export type DiffMarkerVariant = 'added' | 'removed'

// U+2029 PARAGRAPH SEPARATOR. The host collapses a marker's inner paragraph
// breaks to this sentinel so the whole region survives markdown parsing as one
// HTML block (a real blank line would terminate the block early); the marker
// re-expands it into separate paragraphs. Effectively absent from real content.
export const PARAGRAPH_SEPARATOR = '\u2029'

const VARIANTS: Record<DiffMarkerVariant, { tint: string; heading: string; title: string; icon: LucideIcon }> = {
  added: {
    tint: 'bg-success-subtle',
    heading: 'text-success-subtle',
    title: 'New Content',
    icon: PlusIcon,
  },
  removed: {
    tint: 'bg-danger-subtle',
    heading: 'text-danger-subtle',
    title: 'Removed Content',
    icon: MinusIcon,
  },
}

export const diffMarkerWrapperClass = '-mx-2 -ml-2.5'

// Tags that mark the editable slot the host projects content into. A marker
// renders the injected slot only when it is, or wraps, one of these; otherwise
// it shows its own inner text read-only.
export const SLOT_TAGS = ['content', 'insertion_point']

// The read-only renderer sees the same slot tags hyphenated; see `hyphenateMarkerTags`.
export const HYPHENATED_SLOT_TAGS = ['content', 'insertion-point']

interface DiffMarkerBoxProps {
  variant: DiffMarkerVariant
  /** The marker's inner text, paragraphs joined by `PARAGRAPH_SEPARATOR`. */
  label: string
  /** The marker wraps a slot tag, so it hosts the injected content instead of its own text. */
  hostsSlot: boolean
}

/** The coloured region both renderers draw: the Tiptap node view and the read-only markdown. */
export function DiffMarkerBox({ variant, label, hostsSlot }: DiffMarkerBoxProps) {
  const { tint, heading, title, icon: Icon } = VARIANTS[variant]
  const injected = useContext(ContentSlotContext)
  // Re-expand the paragraph sentinel the host collapsed the region into.
  const paragraphs = label ? label.split(PARAGRAPH_SEPARATOR) : []
  const labelBody = paragraphs.length ? (
    <Markdown.Prose size="sm" className="text-foreground">
      {paragraphs.map((paragraph, index) => (
        <p key={index}>{paragraph}</p>
      ))}
    </Markdown.Prose>
  ) : null

  return (
    <div className="bg-background w-full">
      <div className={`${tint} w-full px-3 py-2 text-sm font-normal`}>
        <div className={`${heading} flex items-center gap-1 text-xs`}>
          <Icon size={10} />
          {title}
        </div>
        {hostsSlot ? (injected ?? labelBody) : labelBody}
      </div>
    </div>
  )
}
